import React, { useEffect, useMemo, useRef } from "react";
import { 
  Box, 
  Typography, 
  Avatar, 
  Paper, 
  Stack,
  IconButton,
  Chip,
  Tooltip
} from "@mui/material";
import PropTypes from 'prop-types';
import PersonIcon from "@mui/icons-material/Person";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import LabelOutlinedIcon from "@mui/icons-material/LabelOutlined";
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { getAuthenticatedAvatarUrl, useAvatarBlobSubscription } from 'Shared/Utils/Domain/Services/AvatarUrlService';

const OrgChartListView = ({ 
  data, 
  onNodeClick, 
  highlightNodeId, 
  onExpandNode, 
  loadingNodeIds, 
  initialCollapsedIds, 
  collapsedIds, 
  onToggleNode, 
  displayMode = 'default' 
}) => {
  useAvatarBlobSubscription();
  const nodeRefs = useRef({});
  const [internalCollapsedIds, setInternalCollapsedIds] = React.useState(new Set());
  const effectiveCollapsedIds = collapsedIds || internalCollapsedIds;
  const appliedInitialRef = useRef(false);
  const isSpecialMode = displayMode === 'holidays';

  // Seed collapsed IDs from parent when data first loads
  useEffect(() => {
    if (initialCollapsedIds && initialCollapsedIds.size > 0 && !appliedInitialRef.current) {
      setInternalCollapsedIds(new Set(initialCollapsedIds));
      appliedInitialRef.current = true;
    }
  }, [initialCollapsedIds]);

  const normalizeId = (value) => {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const id = Number(value);
    return Number.isNaN(id) ? null : id;
  };

  const { childrenByParentId, rootNodes } = useMemo(() => {
    const map = new Map();
    const idsInData = new Set();

    data.forEach((node) => {
      const nodeId = normalizeId(node.id);
      if (nodeId !== null) {
        idsInData.add(nodeId);
      }
    });

    data.forEach((node) => {
      const parentId = normalizeId(node.parent);
      const key = parentId ?? 0;
      if (!map.has(key)) {
        map.set(key, []);
      }
      map.get(key).push(node);
    });

    let roots = data.filter((node) => {
      const parentId = normalizeId(node.parent);
      return parentId === null || parentId === 0;
    });

    if (roots.length === 0) {
      roots = data.filter((node) => {
        const parentId = normalizeId(node.parent);
        return parentId === null || parentId === 0 || !idsInData.has(parentId);
      });
    }

    return { childrenByParentId: map, rootNodes: roots };
  }, [data]);

  useEffect(() => {
    const targetId = normalizeId(highlightNodeId);
    if (targetId !== null) {
      // Find ancestors and expand them
      const ancestors = [];
      let currentId = targetId;
      
      const findParent = (id) => {
        const node = data.find(n => normalizeId(n.id) === id);
        return node ? normalizeId(node.parent) : null;
      };

      let parentId = findParent(currentId);
      while (parentId !== null && parentId !== 0) {
        ancestors.push(parentId);
        currentId = parentId;
        parentId = findParent(currentId);
      }

      if (ancestors.length > 0) {
        if (onToggleNode) {
          // Ancestors are already being expanded in PublicOrgChartView via setCollapsedIds
        } else {
          setInternalCollapsedIds(prev => {
            const next = new Set(prev);
            ancestors.forEach(id => next.delete(id));
            return next;
          });
        }
      }

      // Scroll to element after state update
      setTimeout(() => {
        if (nodeRefs.current[targetId]) {
          nodeRefs.current[targetId].scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }
  }, [highlightNodeId, data]);

  const toggleNode = async (node, id, event, hasLoadedChildren) => {
    event.stopPropagation();

    if (!hasLoadedChildren && typeof onExpandNode === 'function') {
      await onExpandNode(node);
      return;
    }

    if (onToggleNode) {
      onToggleNode(id);
    } else {
      setInternalCollapsedIds(prev => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
    }
  };

  if (!data || data.length === 0) return null;

  const renderNode = (node, level = 0) => {
    const nodeId = normalizeId(node.id);
    const children = childrenByParentId.get(nodeId ?? 0) || [];
    const isPositionLinked = !!node.positionId;
    const isOfficialPosition = Boolean(node.isOfficialPosition || (node.positionId && node.jobPositionId));
    const isCustomPosition = Boolean(isPositionLinked && !isOfficialPosition);
    const isHighlighted = normalizeId(highlightNodeId) === nodeId;
    const hasLoadedChildren = children.length > 0;
    const isCollapsed = hasLoadedChildren ? effectiveCollapsedIds.has(nodeId) : true;
    const hasChildren = hasLoadedChildren || !!node.hasChildren;
    const isLoadingChildren = loadingNodeIds?.has(nodeId);

    const mainTitle = isPositionLinked ? node.position : node.text;
    const subTitle = !isSpecialMode && isPositionLinked ? node.user : null;
    const avatarUrl = !isSpecialMode && isPositionLinked ? getAuthenticatedAvatarUrl(node.userAvatar) : null;
    const indentSteps = level; 
    
    return (
      <Box 
        key={node.id}
        ref={el => {
          if (nodeId !== null) {
            nodeRefs.current[nodeId] = el;
          }
        }}
        sx={{ ml: indentSteps > 0 ? 3 : 0, position: 'relative' }}
      >
        {/* Linea vertical de conexion */}
        {level > 0 && (
          <Box
            sx={{
              position: 'absolute',
              left: -18,
              top: -6, 
              bottom: hasChildren && !isCollapsed ? 'auto' : 14, 
              height: hasChildren && !isCollapsed ? 28 : 'auto',
              width: '1px',
              bgcolor: 'primary.main',
              opacity: 0.15,
              zIndex: 0
            }}
          />
        )}
        
        {/* Linea horizontal de conexion */}
        {level > 0 && (
          <Box
            sx={{
              position: 'absolute',
              left: -18,
              top: 17,
              width: 18,
              height: '1px',
              bgcolor: 'primary.main',
              opacity: 0.15,
              zIndex: 0
            }}
          />
        )}

        <Paper
          elevation={0}
          onClick={() => onNodeClick && onNodeClick(node)}
          sx={{
            p: 0.6,
            mb: 0.6,
            borderRadius: 1,
            border: isCustomPosition ? '1px dashed' : '1px solid',
            borderColor: isHighlighted
              ? 'primary.main'
              : isCustomPosition
              ? '#ed6c02'
              : 'rgba(0,0,0,0.08)',
            boxShadow: isHighlighted ? '0 0 0 2px rgba(25, 118, 210, 0.1)' : 'none',
            transition: 'all 0.15s ease-in-out',
            bgcolor: isHighlighted ? '#f5faff' : isCustomPosition ? '#fffbf5' : '#ffffff',
            cursor: 'pointer',
            position: 'relative',
            zIndex: 1,
            '&:hover': {
              borderColor: isCustomPosition ? '#ed6c02' : 'primary.light',
              bgcolor: isHighlighted ? '#f5faff' : isCustomPosition ? '#fff6eb' : '#fafafa',
              transform: 'translateX(2px)'
            }
          }}
        >
          <Stack direction="row" spacing={0.8} alignItems="center">
            {/* Boton Expandir/Contraer con estilo minimalista */}
            <Box sx={{ width: 22, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              {hasChildren ? (
                <IconButton 
                  size="small" 
                  onClick={(e) => toggleNode(node, nodeId, e, hasLoadedChildren)}
                  sx={{ 
                    p: 0.1, 
                    color: 'primary.main',
                    bgcolor: 'primary.50',
                    borderRadius: 0.5,
                    '&:hover': { bgcolor: 'primary.100' }
                  }}
                >
                  {isCollapsed ? <AddIcon sx={{ fontSize: 13 }} /> : <RemoveIcon sx={{ fontSize: 13 }} />}
                </IconButton>
              ) : (
                <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: 'divider' }} />
              )}
            </Box>

            {isPositionLinked ? (
              <Avatar 
                src={avatarUrl} 
                sx={{ 
                  width: 28, 
                  height: 28, 
                  bgcolor: isCustomPosition ? '#ff9800' : 'primary.main',
                  fontSize: '0.7rem',
                  border: '1px solid white',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
                }}
              >
                {isCustomPosition
                  ? <LabelOutlinedIcon sx={{ fontSize: 15 }} />
                  : <PersonIcon sx={{ fontSize: 16 }} />}
              </Avatar>
            ) : (
              <Box sx={{ 
                width: 28, 
                height: 28, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                borderRadius: 0.5,
                bgcolor: 'grey.100',
                color: 'grey.600'
              }}>
                <AccountTreeIcon sx={{ fontSize: 14 }} />
              </Box>
            )}

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Tooltip title={mainTitle || ''} arrow placement="top" enterDelay={250} disableInteractive>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      fontWeight: 'bold', 
                      lineHeight: 1.1, 
                      color: isHighlighted ? 'primary.main' : isCustomPosition ? '#d84315' : 'rgba(0,0,0,0.8)',
                      display: 'block',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}
                  >
                    {mainTitle || 'Sin título'}
                  </Typography>
                </Tooltip>
                {isCustomPosition && (
                  <Chip
                    label="Personalizado"
                    size="small"
                    variant="outlined"
                    color="warning"
                    sx={{ height: 16, fontSize: '0.58rem', px: 0.2, py: 0 }}
                  />
                )}
              </Stack>
              {subTitle && (
                <Tooltip title={subTitle} arrow placement="bottom" enterDelay={250} disableInteractive>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      display: 'block', 
                      fontSize: '0.65rem',
                      color: 'text.secondary',
                      lineHeight: 1,
                      mt: 0.2,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}
                  >
                    {subTitle}
                  </Typography>
                </Tooltip>
              )}
              {isLoadingChildren && (
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.62rem', display: 'block' }}>
                  Cargando...
                </Typography>
              )}
            </Box>
          </Stack>
        </Paper>

        {hasChildren && !isCollapsed && (
          <Box sx={{ mt: 0 }}>
            {children.map(child => renderNode(child, level + 1))}
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ py: 1, px: 1, minWidth: 680 }}>
      {rootNodes.map(rootNode => renderNode(rootNode))}
    </Box>
  );
};

OrgChartListView.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    text: PropTypes.string,
    position: PropTypes.string,
    positionId: PropTypes.number,
    jobPositionId: PropTypes.number,
    isOfficialPosition: PropTypes.bool,
    user: PropTypes.string,
    userAvatar: PropTypes.string,
    parent: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    hasChildren: PropTypes.bool
  })),
  onNodeClick: PropTypes.func,
  highlightNodeId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onExpandNode: PropTypes.func,
  loadingNodeIds: PropTypes.instanceOf(Set),
  initialCollapsedIds: PropTypes.instanceOf(Set),
  collapsedIds: PropTypes.instanceOf(Set),
  onToggleNode: PropTypes.func,
  displayMode: PropTypes.oneOf(['default', 'holidays', 'vacaciones', 'extras'])
};

export default OrgChartListView;